import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { getCopilotProcessWelcomeMessage } from 'components/Copilot/copilotProcessMessages';
import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  ContentBlockKind,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotScene } from 'models/copilot/copilotScene';
import { CopilotFrameworkAuditContent } from 'models/copilot/Processes/FrameworkAudit/copilotFrameworkAudit';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import Norm from 'models/norm';
import { apiCopilotExecuteProcessFrameworkAudit } from 'services/Api/copilotService';
import { newGuid } from 'utils/guid';
import { getSceneBindings, withSceneHelpFirst } from '../Generic/genericSceneBindings';
import {
  getCachedActiveFrameworkAudits,
  isFrameworkAuditRunning,
  notifyFrameworkAuditsChanged,
} from './useActiveFrameworkAudits';

const standardSelectionBlockTitle = 'frameworkAuditStandardSelection';
const startActionName = 'frameworkAuditStart';

interface IFrameworkAuditSelection {
  isoNormId: number;
  includeDocumentation: boolean;
  includeEvidence: boolean;
  sendCompletionEmail: boolean;
}

// The framework audit usage: audit every control and requirement of one standard. The standard is
// picked in the chat via an adaptive card BEFORE the first process execute - the process-state row
// is keyed on the standard, so the process cannot ask for its own entity and re-key itself. The
// card submit re-enters getAnswer with the inputs on the block; from there every execute carries
// (Standard, isoNormId).
export class CopilotFrameworkAuditPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.FrameworkAudit;
    this.name = 'FrameworkAudit';
    this.supportsChat = true;
    this.requiresActiveAction = true;
  }

  // A whole framework takes long and the run does not need the panel: the user may start other
  // copilot work while it finishes.
  getBindings(_content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return withSceneHelpFirst([
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.FrameworkAudit, runsInBackground: true }),
      ...getSceneBindings(CopilotScene.Standards),
    ]);
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return getCopilotProcessWelcomeMessage(action, t);
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;
    const auditContent = content as CopilotFrameworkAuditContent;
    if (!auditContent.isoNormId) return undefined;

    return { entityType: EntityTypes.Standard, entityId: auditContent.isoNormId };
  }

  canStartChat(_actions: CopilotAction[]): boolean {
    return false;
  }

  // Starting the audit is this usage's own card action: say that the preparation is starting (it can
  // take a while) and let the panel run getAnswer, which sends the choice to the server. Returning
  // false for any other card keeps the shared panel behaviour untouched.
  async onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    t: TFunction<string[]>,
  ): Promise<boolean> {
    if (payload.actionData.action !== startActionName) return false;

    // getAnswer re-reads THIS conversation, so the choice has to be on this object's block before the
    // round trip - otherwise a submit that accepted every default looks like "nothing selected" and
    // the panel just asks for the standard again.
    this.applySubmittedInputs(runtime, block.id, payload.inputs);

    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.response = t('copilot:FrameworkAudit.PreparationStarting');
    runtime.conversation.thread.push(item);
    // A clone, not this same instance: publishing the object that is already rendered can be skipped
    // as an identity no-op, and the clone carries both the inputs and the new item.
    runtime.setConversation(runtime.conversation.clone());

    return true;
  }

  async getAnswer(runtime: CopilotPanelRuntime, t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (!action || action.processId !== TenantProcess.FrameworkAudit) {
      return;
    }

    const content = runtime.content as CopilotFrameworkAuditContent | undefined;
    if (!content) return;

    const selection = this.getStandardSelection(runtime) ?? this.takeSingleStandard(runtime, t);
    if (!selection) {
      this.addStandardSelectionQuestion(runtime, t);

      return;
    }

    // Entity-bound reads (persisted state, stop) address the process row by this id.
    content.isoNormId = selection.isoNormId;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessFrameworkAudit(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      selection.isoNormId,
      selection.includeDocumentation,
      selection.includeEvidence,
      selection.sendCompletionEmail,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    // The run is unattended: tell the shared status store to re-read so the "AI audit running" markers
    // on the frameworks scene and the dashboard widgets light up in this visit, not after a navigation.
    notifyFrameworkAuditsChanged(runtime.appContext);

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
  }

  // The answered selection card, wherever it is in the thread: the choice survives panel reloads
  // because the conversation is part of the persisted process state.
  private getStandardSelection(runtime: CopilotPanelRuntime): IFrameworkAuditSelection | undefined {
    for (let index = runtime.conversation.thread.length - 1; index >= 0; index--) {
      const item = runtime.conversation.thread[index];
      const block = item.contentBlocks?.find(
        (candidate) =>
          candidate.kind === ContentBlockKind.AdaptiveCard && candidate.title === standardSelectionBlockTitle,
      );
      if (!block?.adaptiveInputs) continue;

      const isoNormId = Number(block.adaptiveInputs['selectedStandard']);
      if (!Number.isFinite(isoNormId) || isoNormId <= 0) continue;

      return {
        isoNormId,
        includeDocumentation: block.adaptiveInputs['includeDocumentation'] !== 'false',
        includeEvidence: block.adaptiveInputs['includeEvidence'] !== 'false',
        sendCompletionEmail: block.adaptiveInputs['sendCompletionEmail'] !== 'false',
      };
    }

    // The scene seeds the content with a standard whose audit is running, and getAnswer stamps it for
    // the entity-bound reads. Either way the id may only ATTACH to a run that is in progress right now
    // - it must never start one, or clicking the action after a run finished would silently relaunch a
    // full audit of the previous standard instead of asking which framework to audit.
    const content = runtime.content as CopilotFrameworkAuditContent | undefined;
    if (content?.isoNormId && isFrameworkAuditRunning(getCachedActiveFrameworkAudits(), content.isoNormId)) {
      return {
        isoNormId: content.isoNormId,
        includeDocumentation: true,
        includeEvidence: true,
        sendCompletionEmail: true,
      };
    }

    return undefined;
  }

  private getActiveStandards(runtime: CopilotPanelRuntime): Norm[] {
    return runtime.appContext.globalDataCache.norms.items.filter(
      (norm) => norm.active && norm.isoNormId !== undefined && norm.isoNormId > 0,
    );
  }

  // With exactly one activated framework there is nothing to choose: say which one is audited and run
  // it with every option on, instead of a card with a single choice. The started message is pushed
  // onto the conversation the way the card submit does, so the run that follows sees it.
  private takeSingleStandard(
    runtime: CopilotPanelRuntime,
    t: TFunction<string[]>,
  ): IFrameworkAuditSelection | undefined {
    const standards = this.getActiveStandards(runtime);
    if (standards.length !== 1 || !standards[0].isoNormId) return undefined;

    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.response = t('copilot:FrameworkAudit.SingleStandard', { name: standards[0].name });
    runtime.conversation.thread.push(item);
    runtime.setConversation(runtime.conversation.clone());

    return {
      isoNormId: standards[0].isoNormId,
      includeDocumentation: true,
      includeEvidence: true,
      sendCompletionEmail: true,
    };
  }

  private addStandardSelectionQuestion(runtime: CopilotPanelRuntime, t: TFunction<string[]>): void {
    const standards = this.getActiveStandards(runtime);

    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.CopilotQuestion;

    if (standards.length === 0) {
      item.type = CopilotConversionItemType.Copilot;
      item.response = t('copilot:FrameworkAudit.NoStandards');
      runtime.setConversation((prev) => {
        const next = (prev ?? runtime.conversation).clone();
        next.thread.push(item);

        return next;
      });

      return;
    }

    const block = new CopilotConversationContentBlock();
    block.id = newGuid();
    block.kind = ContentBlockKind.AdaptiveCard;
    block.title = standardSelectionBlockTitle;
    block.adaptiveCardJson = JSON.stringify({
      $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
      type: 'AdaptiveCard',
      version: '1.5',
      body: [
        { type: 'TextBlock', text: t('copilot:FrameworkAudit.ChooseStandard'), wrap: true },
        {
          type: 'Input.ChoiceSet',
          id: 'selectedStandard',
          style: 'expanded',
          isMultiSelect: false,
          value: `${standards[0].isoNormId}`,
          choices: standards.map((norm) => ({ title: norm.name, value: `${norm.isoNormId}` })),
        },
        {
          type: 'Input.Toggle',
          id: 'includeDocumentation',
          title: t('copilot:FrameworkAudit.IncludeDocumentation'),
          value: 'true',
          valueOn: 'true',
          valueOff: 'false',
        },
        {
          type: 'Input.Toggle',
          id: 'includeEvidence',
          title: t('copilot:FrameworkAudit.IncludeEvidence'),
          value: 'true',
          valueOn: 'true',
          valueOff: 'false',
        },
        {
          type: 'Input.Toggle',
          id: 'sendCompletionEmail',
          title: t('copilot:FrameworkAudit.SendCompletionEmail'),
          value: 'true',
          valueOn: 'true',
          valueOff: 'false',
        },
      ],
      actions: [
        {
          type: 'Action.Submit',
          title: t('copilot:FrameworkAudit.Start'),
          data: { action: startActionName },
        },
      ],
    });
    item.contentBlocks = [block];

    runtime.setConversation((prev) => {
      const next = (prev ?? runtime.conversation).clone();
      next.thread.push(item);

      return next;
    });
  }
}
